import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { formatINR } from "../utils/format";
import { Sparkles, AlertTriangle, AlertOctagon, Info, Phone, TrendingUp, RefreshCcw } from "lucide-react";

const SEVERITY = {
  critical: {
    border: "border-l-[hsl(var(--critical))]",
    bg: "bg-[hsl(var(--critical))]/5",
    label: "text-[hsl(var(--critical))]",
    Icon: AlertOctagon,
  },
  warning: {
    border: "border-l-[hsl(var(--warning))]",
    bg: "bg-[hsl(var(--warning))]/5",
    label: "text-[hsl(var(--warning))]",
    Icon: AlertTriangle,
  },
  info: {
    border: "border-l-[hsl(var(--ai))]",
    bg: "bg-[hsl(var(--ai))]/5",
    label: "text-[hsl(var(--ai))]",
    Icon: Info,
  },
};

export default function StructuredInsightsPanel({ clientId = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = clientId ? { client_id: clientId } : {};
      const { data: d } = await api.get("/ai/insights/structured", { params });
      setData(d);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load structured insights");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  if (loading && !data) {
    return (
      <div className="bg-card border border-border rounded-sm p-6 text-sm text-muted-foreground" data-testid="structured-insights-loading">
        Computing structured insights from MongoDB…
      </div>
    );
  }
  if (err) {
    return (
      <div className="bg-card border border-border rounded-sm p-6 text-sm text-[hsl(var(--critical))]" data-testid="structured-insights-error">
        {err}
      </div>
    );
  }
  if (!data) return null;

  const k = data.kpis || {};
  const priorities = data.priorities || [];

  // Derived deltas (week-over-week)
  const wow = (curr, prev) => {
    if (!prev) return null;
    return ((curr - prev) / prev) * 100;
  };

  return (
    <div className="space-y-6" data-testid="structured-insights">
      {/* Headline */}
      <div className="bg-card border border-border border-t-2 border-t-[hsl(var(--ai))] rounded-sm p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <Sparkles className="w-4 h-4 text-[hsl(var(--ai))]" />
              <div className="label-mono text-[hsl(var(--ai))]">Structured Insights · Deterministic</div>
            </div>
            <div className="font-heading text-2xl tracking-tight">{data.headline}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Computed from {k.active_campaigns} active campaign{k.active_campaigns === 1 ? "" : "s"}, last 7 days · {k.critical_alerts} open critical alert{k.critical_alerts === 1 ? "" : "s"}
            </p>
          </div>
          <button
            onClick={load}
            data-testid="structured-insights-refresh"
            className="h-9 px-3 inline-flex items-center gap-1.5 text-xs font-medium border border-border bg-card hover:bg-secondary rounded-sm"
          >
            <RefreshCcw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="structured-insights-kpis">
        <KpiTile label="Spend · today" value={formatINR(k.spend_today, { compact: true })} sub={`MTD ${formatINR(k.spend_mtd, { compact: true })}`} />
        <KpiTile label="Leads · today" value={k.leads_today} sub={`7d: ${k.leads_7d} (was ${k.leads_prev7})`} delta={wow(k.leads_7d, k.leads_prev7)} reverse />
        <KpiTile
          label="Phone calls · today"
          value={k.phone_today}
          sub={`7d: ${k.phone_7d} (was ${k.phone_prev7})`}
          delta={wow(k.phone_7d, k.phone_prev7)}
          icon={Phone}
          reverse
        />
        <KpiTile label="CPL · 7d" value={k.cpl_7d ? formatINR(k.cpl_7d) : "—"} sub={`Prev 7d ${k.cpl_prev7 ? formatINR(k.cpl_prev7) : "—"}`} delta={wow(k.cpl_7d, k.cpl_prev7)} />
        <KpiTile label="Active campaigns" value={`${k.active_campaigns}/${k.total_campaigns}`} sub="Active / total" />
        <KpiTile
          label="Monthly pacing"
          value={`${(k.monthly_pacing_pct ?? 0).toFixed(0)}%`}
          sub={`Expected ~${(k.monthly_pacing_expected_pct ?? 0).toFixed(0)}%`}
          tone={
            k.monthly_pacing_pct >= 80 && k.monthly_pacing_pct >= (k.monthly_pacing_expected_pct ?? 0) + 15
              ? "critical"
              : k.monthly_pacing_pct >= 80
              ? "warning"
              : "primary"
          }
        />
        <KpiTile label="Critical alerts" value={k.critical_alerts} tone={k.critical_alerts > 0 ? "critical" : "success"} />
        <KpiTile label="Warning alerts" value={k.warning_alerts} tone={k.warning_alerts > 0 ? "warning" : "primary"} />
      </div>

      {/* Priorities */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-4 h-4 text-muted-foreground" />
          <div className="label-mono">Priorities</div>
          <span className="text-xs text-muted-foreground">· severity-ordered</span>
        </div>
        {priorities.length === 0 ? (
          <div className="bg-card border border-border rounded-sm p-6 text-sm text-muted-foreground text-center" data-testid="structured-insights-empty">
            No prioritised actions available yet — run a sync first or wait for the next 15-min alert tick.
          </div>
        ) : (
          <div className="space-y-2" data-testid="structured-insights-priorities">
            {priorities.map((p, i) => {
              const conf = SEVERITY[p.severity] || SEVERITY.info;
              const Icon = conf.Icon;
              return (
                <div
                  key={i}
                  className={`bg-card border border-border border-l-2 ${conf.border} ${conf.bg} rounded-sm p-4`}
                  data-testid={`structured-insights-priority-${i}`}
                >
                  <div className="flex items-start gap-3">
                    <Icon className={`w-4 h-4 mt-0.5 ${conf.label}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className={`text-[10px] font-bold uppercase tracking-wider ${conf.label}`}>{p.severity}</span>
                      </div>
                      <div className="font-medium text-sm">{p.title}</div>
                      <div className="text-xs text-muted-foreground mt-1">{p.detail}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Top campaigns */}
      {data.top_campaigns?.length > 0 && (
        <div>
          <div className="label-mono mb-2">Top campaigns · last 7d (by leads)</div>
          <div className="bg-card border border-border rounded-sm overflow-hidden" data-testid="structured-insights-top-campaigns">
            <table className="w-full data-table">
              <thead><tr><th>Campaign</th><th style={{ width: 100 }} className="text-right">Leads · 7d</th></tr></thead>
              <tbody>
                {data.top_campaigns.map((c) => (
                  <tr key={c.id}>
                    <td className="font-medium">{c.name}</td>
                    <td className="text-right tabular font-semibold">{c.leads}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function KpiTile({ label, value, sub, tone = "primary", delta = null, reverse = false, icon: Icon }) {
  const borderColor = {
    primary: "border-border",
    success: "border-l-2 border-l-[hsl(var(--success))]",
    warning: "border-l-2 border-l-[hsl(var(--warning))]",
    critical: "border-l-2 border-l-[hsl(var(--critical))]",
  }[tone];

  let deltaEl = null;
  if (delta != null && Number.isFinite(delta) && Math.abs(delta) >= 0.5) {
    const isUp = delta > 0;
    // For metrics where down is bad (leads/calls), reverse=true → bad if down
    const isBad = reverse ? !isUp : isUp;
    const cls = isBad ? "text-[hsl(var(--critical))]" : "text-[hsl(var(--success))]";
    deltaEl = (
      <span className={`text-[11px] font-semibold ml-1 ${cls}`}>
        {isUp ? "▲" : "▼"} {Math.abs(delta).toFixed(0)}%
      </span>
    );
  }

  return (
    <div className={`bg-card border rounded-sm p-3.5 ${borderColor}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="label-mono">{label}</div>
        {Icon && <Icon className="w-3.5 h-3.5 text-muted-foreground" />}
      </div>
      <div className="text-xl font-semibold tabular leading-tight flex items-baseline">
        <span>{value}</span>
        {deltaEl}
      </div>
      {sub && <div className="text-[11px] text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}
